import React, { useState, useMemo } from "react";
import { injectIntl, FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import { Timeline } from "./timeline-partial";
import ScreenPosition from "components/screen-position";
import FormHelperText from "@material-ui/core/FormHelperText";
import Typography from "@material-ui/core/Typography";
import FormTextField from "components/form/form-textfield";
import withStyles from "components/with-styles";
import styles from "./styles/timeline-with-asset-preview.css.js";
import { getTimelineElements, isOnTime, getValidLayouts } from "./timeline-partial";

const TimelineWithAssetPreview = ({
  classes,
  intl,
  showTitle = false,
  displayOnly,
  currentSchedule,
  schedules,
  layouts,
  customerId,
  onChange,
}) => {
  const elements = useMemo(
    () => getTimelineElements({ layouts: getValidLayouts(layouts), currentSchedule, displayOnly }),
    [currentSchedule, displayOnly, layouts],
  );
  const [currentTime, setCurrentTime] = useState(0);

  const handleTimelineElementChange = element => {
    if (layouts && layouts.length > 0) {
      const layoutIndex = layouts.findIndex(layout => layout.id === element.layoutId);

      onChange(`layouts[${layoutIndex}].segments`)([
        ...layouts[layoutIndex].segments.filter(segment => segment.type !== currentSchedule.type),
        ...element.segments,
      ]);
    }
  };

  const handlePeriodChange = name => value => {
    const scheduleIndex = schedules.findIndex(schedule => schedule.type === currentSchedule.type);
    onChange(`schedules[${scheduleIndex}].period`)(parseInt(value, 10));
  };

  return (
    <div>
      {showTitle && (
        <div className={classes.title}>
          <Typography variant="h6" gutterBottom component="h6">
            {currentSchedule.type}
          </Typography>
        </div>
      )}

      <ScreenPosition
        width={500}
        layouts={elements
          .filter(isOnTime(currentTime))
          .map(element => layouts.find(layout => layout.id === element.layoutId || layout.assetId === element.layoutId))
          .map(layout => ({
            ...layout,
            asset: {
              ...layout.asset,
              customerId: layout.asset ? layout.asset.customerId || customerId : customerId,
            },
          }))}
      />
      <div className={classes.timeline}>
        <Timeline
          displayOnly={displayOnly}
          type={currentSchedule.type}
          period={currentSchedule.period}
          onSelectTime={time => setCurrentTime(time)}
          onChange={handleTimelineElementChange}
          elements={elements}
        />
      </div>
      <div>
        <FormHelperText>
          <FormattedMessage id="timeline.axis.click.preview" />
        </FormHelperText>
      </div>
      {!displayOnly && (
        <div className={classes.period}>
          <FormTextField
            className={classes.period}
            type="number"
            name="period"
            inputProps={{ min: 1 }}
            value={currentSchedule.period}
            onChange={handlePeriodChange}
            onFocus={event => event.target.select()}
            label={intl.formatMessage({ id: "timeline.period" })}
          />
        </div>
      )}
    </div>
  );
};

TimelineWithAssetPreview.propTypes = {
  /**
   * Schedule a representar
   */
  currentSchedule: PropTypes.object,
  /**
   * Layouts del graphicset
   */
  layouts: PropTypes.array,
  /**
   * Id del customer del graphicset
   */
  customerId: PropTypes.string,
  /**
   * Callback que se dispara con el cambio del input
   */
  onChange: PropTypes.func,
  /**
   * Indica si se muestra o no el tipo del schedule como título
   */
  showTitle: PropTypes.bool,
  /**
   * Indica si está deshabilitada la edición o no
   */
  displayOnly: PropTypes.bool,
  /**
   * Conjunto de schedules del graphicset
   */
  schedules: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string.isRequired,
      period: PropTypes.number.isRequired,
    }),
  ),
};

export default withStyles(styles)(injectIntl(TimelineWithAssetPreview));
